import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/adminService';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './AdminPage.css';

const ORDER_STATUSES = ['pending', 'confirmed', 'in_progress', 'completed', 'cancelled', 'refunded'];
const PAYMENT_STATUSES = ['pending', 'processing', 'completed', 'failed', 'refunded'];

const STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
  not_started: 'Not Started',
  started: 'Started',
};

const STATUS_CLASS = {
  pending: 'badge-pending',
  confirmed: 'badge-confirmed',
  in_progress: 'badge-progress',
  completed: 'badge-completed',
  cancelled: 'badge-cancelled',
  refunded: 'badge-refunded',
  not_started: 'badge-ready',
  started: 'badge-progress',
};

const StatusBadge = ({ status }) => (
  <span className={`admin-badge ${STATUS_CLASS[status] || 'badge-default'}`}>
    {STATUS_LABELS[status] || status}
  </span>
);

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const shortId = (id) => (id ? id.slice(-8).toUpperCase() : '—');

// ─── Assign Partner Modal ────────────────────────────────────────────────────
const AssignPartnerModal = ({ orderId, currentPartnerId, onClose, onAssigned }) => {
  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi
      .getPartners()
      .then((res) => setPartners(res.data || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = partners.filter((p) => {
    const q = search.toLowerCase();
    return (
      p.name?.toLowerCase().includes(q) ||
      p.phone_number?.includes(q) ||
      p.business_name?.toLowerCase().includes(q)
    );
  });

  const handleAssign = async (partnerId) => {
    setAssigning(true);
    setError('');
    try {
      await adminApi.assignPartner(orderId, partnerId);
      onAssigned();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Assign Partner — Order #{shortId(orderId)}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-search">
          <input
            type="text"
            placeholder="Search by name or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        {error && <div className="admin-alert admin-alert-error">{error}</div>}

        <div className="partner-list">
          {loading ? (
            <div className="modal-loading"><div className="spinner small"></div> Loading partners…</div>
          ) : filtered.length === 0 ? (
            <p className="modal-empty">No partners found.</p>
          ) : (
            filtered.map((p) => (
              <div
                key={p.id}
                className={`partner-item ${p.id === currentPartnerId ? 'partner-item-current' : ''}`}
              >
                <div className="partner-item-info">
                  <span className="partner-item-name">{p.name}</span>
                  {p.business_name && <span className="partner-item-biz">{p.business_name}</span>}
                  <span className="partner-item-phone">{p.phone_number}</span>
                  <span className="partner-item-meta">
                    ★ {Number(p.average_rating || 0).toFixed(1)} · {p.total_jobs_completed || 0} jobs
                  </span>
                  <StatusBadge status={p.status} />
                </div>
                <button
                  className={`btn-assign ${p.id === currentPartnerId ? 'btn-current' : ''}`}
                  onClick={() => handleAssign(p.id)}
                  disabled={assigning || p.id === currentPartnerId}
                >
                  {p.id === currentPartnerId ? 'Current' : 'Assign'}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Edit Order Modal ────────────────────────────────────────────────────────
const EditOrderModal = ({ order, onClose, onUpdated }) => {
  const [status, setStatus] = useState(order.status);
  const [paymentStatus, setPaymentStatus] = useState(order.payment_status);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await adminApi.updateOrder(order.id, { status, payment_status: paymentStatus });
      onUpdated();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box modal-box-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Update Order #{shortId(order.id)}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="admin-alert admin-alert-error">{error}</div>}

        <div className="edit-form">
          <div className="edit-field">
            <label>Order Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {ORDER_STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
              ))}
            </select>
          </div>
          <div className="edit-field">
            <label>Payment Status</label>
            <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
              {PAYMENT_STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn-modal-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-modal-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Confirm Dialog ──────────────────────────────────────────────────────────
const ConfirmDialog = ({ message, onConfirm, onCancel, confirming }) => (
  <div className="modal-overlay" onClick={onCancel}>
    <div className="modal-box modal-box-sm confirm-box" onClick={(e) => e.stopPropagation()}>
      <p className="confirm-message">{message}</p>
      <div className="modal-actions">
        <button className="btn-modal-cancel" onClick={onCancel} disabled={confirming}>Cancel</button>
        <button className="btn-modal-danger" onClick={onConfirm} disabled={confirming}>
          {confirming ? 'Processing…' : 'Confirm'}
        </button>
      </div>
    </div>
  </div>
);

// ─── Order Detail Drawer ─────────────────────────────────────────────────────
const OrderDetailDrawer = ({ orderId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi
      .getOrderDetails(orderId)
      .then((res) => setDetail(res.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [orderId]);

  const parseAddress = (addr) => {
    if (!addr) return null;
    if (typeof addr === 'string') { try { return JSON.parse(addr); } catch { return null; } }
    return addr;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box modal-box-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Order Details</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div className="modal-loading"><div className="spinner small"></div> Loading…</div>
        ) : error ? (
          <div className="admin-alert admin-alert-error">{error}</div>
        ) : detail ? (
          <div className="detail-content">
            <div className="detail-grid">
              <div className="detail-col">
                <h4>Order Info</h4>
                <dl>
                  <dt>Order ID</dt><dd>#{shortId(detail.id)}</dd>
                  <dt>Status</dt><dd><StatusBadge status={detail.status} /></dd>
                  <dt>Work Status</dt><dd><StatusBadge status={detail.work_status} /></dd>
                  <dt>Created</dt><dd>{formatDate(detail.created_at)}</dd>
                  <dt>Scheduled</dt><dd>{formatDate(detail.scheduled_date)} · {detail.scheduled_time_label}</dd>
                  <dt>Payment</dt><dd>{detail.payment_method} — <StatusBadge status={detail.payment_status} /></dd>
                  <dt>Total</dt><dd>₹{detail.total_amount}</dd>
                  {detail.final_amount && <><dt>Final</dt><dd>₹{detail.final_amount}</dd></>}
                </dl>
              </div>
              <div className="detail-col">
                <h4>Customer</h4>
                <dl>
                  <dt>Phone</dt><dd>{detail.customer_phone || '—'}</dd>
                </dl>
                <h4>Partner</h4>
                {detail.partner_name ? (
                  <dl>
                    <dt>Name</dt><dd>{detail.partner_name}</dd>
                    {detail.partner_business && <><dt>Business</dt><dd>{detail.partner_business}</dd></>}
                    <dt>Phone</dt><dd>{detail.partner_phone}</dd>
                    <dt>Rating</dt><dd>★ {Number(detail.partner_rating || 0).toFixed(1)}</dd>
                    <dt>Jobs</dt><dd>{detail.partner_jobs || 0}</dd>
                  </dl>
                ) : <p className="na-text">Not assigned</p>}
              </div>
            </div>

            {(() => {
              const addr = parseAddress(detail.address);
              return addr ? (
                <div className="detail-section">
                  <h4>Address</h4>
                  <p className="address-text">
                    {[addr.flatNumber, addr.floor, addr.streetAddress, addr.apartment, addr.city, addr.pin]
                      .filter(Boolean)
                      .join(', ')}
                    {addr.landmark && ` (Near: ${addr.landmark})`}
                  </p>
                </div>
              ) : null;
            })()}

            {detail.items && detail.items.length > 0 && (
              <div className="detail-section">
                <h4>Services Booked</h4>
                <table className="detail-table">
                  <thead>
                    <tr><th>Service</th><th>Category</th><th>Hrs</th><th>₹/hr</th><th>Total</th></tr>
                  </thead>
                  <tbody>
                    {detail.items.map((item) => (
                      <tr key={item.id}>
                        <td>{item.service_name}</td>
                        <td>{item.category?.replace('Services', '').replace(/([A-Z])/g, ' $1').trim()}</td>
                        <td>{item.quantity}</td>
                        <td>₹{item.price_per_hour}</td>
                        <td>₹{item.total_price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

// ─── Main Admin Page ─────────────────────────────────────────────────────────
const AdminPage = () => {
  const { logout, userPhone } = useAuth();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  // Modals
  const [assignModal, setAssignModal] = useState(null); // { orderId, currentPartnerId }
  const [editModal, setEditModal] = useState(null);     // order object
  const [detailDrawer, setDetailDrawer] = useState(null); // orderId
  const [confirmCancel, setConfirmCancel] = useState(null); // orderId
  const [confirmRemove, setConfirmRemove] = useState(null); // orderId
  const [actionLoading, setActionLoading] = useState(false);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const res = await adminApi.getOrders(params);
      setOrders(res.data || []);
      setTotal(res.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, page]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(0);
    fetchOrders();
  };

  const handleCancelOrder = async () => {
    setActionLoading(true);
    try {
      await adminApi.cancelOrder(confirmCancel);
      showToast('Order cancelled successfully');
      setConfirmCancel(null);
      fetchOrders();
    } catch (e) {
      showToast(`Error: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemovePartner = async () => {
    setActionLoading(true);
    try {
      await adminApi.removePartner(confirmRemove);
      showToast('Partner removed successfully');
      setConfirmRemove(null);
      fetchOrders();
    } catch (e) {
      showToast(`Error: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="admin-page">
      <div className="admin-container">
        <Header
          slogan="Admin Dashboard"
          rightContent={
            <div className="admin-header-right">
              <span className="admin-user">{userPhone}</span>
              <button className="admin-logout-btn" onClick={() => { logout(); navigate('/auth'); }}>
                Logout
              </button>
            </div>
          }
        />

        <main className="admin-main">
          {/* Stats bar */}
          <div className="admin-stats">
            <div className="stat-chip">
              <span className="stat-number">{total}</span>
              <span className="stat-label">Total Orders</span>
            </div>
            {['pending', 'confirmed', 'in_progress', 'completed', 'cancelled'].map((s) => {
              const count = orders.filter((o) => o.status === s).length;
              return (
                <div
                  key={s}
                  className={`stat-chip stat-chip-clickable ${statusFilter === s ? 'stat-chip-active' : ''}`}
                  onClick={() => { setStatusFilter(statusFilter === s ? '' : s); setPage(0); }}
                >
                  <span className="stat-number">{count}</span>
                  <span className="stat-label">{STATUS_LABELS[s]}</span>
                </div>
              );
            })}
          </div>

          {/* Filters */}
          <div className="admin-filters">
            <form onSubmit={handleSearchSubmit} className="filter-search">
              <input
                type="text"
                placeholder="Search by order ID or customer phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button type="submit" className="btn-search">Search</button>
            </form>
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <option value="">All Statuses</option>
              {ORDER_STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
              ))}
            </select>
            {(statusFilter || search) && (
              <button
                className="btn-clear-filters"
                onClick={() => { setStatusFilter(''); setSearch(''); setPage(0); }}
              >
                Clear Filters
              </button>
            )}
          </div>

          {toast && <div className="admin-toast">{toast}</div>}

          {error && <div className="admin-alert admin-alert-error">{error}</div>}

          {/* Orders table */}
          {loading ? (
            <div className="admin-loading">
              <div className="spinner"></div>
              <p>Loading orders…</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="admin-empty">
              <div className="empty-icon">📋</div>
              <p>No orders found.</p>
            </div>
          ) : (
            <>
              <div className="table-wrapper">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Date</th>
                      <th>Services</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Work</th>
                      <th>Partner</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id} className={order.status === 'cancelled' ? 'row-cancelled' : ''}>
                        <td>
                          <button
                            className="link-btn"
                            onClick={() => setDetailDrawer(order.id)}
                          >
                            #{shortId(order.id)}
                          </button>
                        </td>
                        <td className="cell-phone">{order.customer_phone || '—'}</td>
                        <td className="cell-date">
                          <span>{formatDate(order.scheduled_date)}</span>
                          <span className="cell-time">{order.scheduled_time_label}</span>
                        </td>
                        <td className="cell-count">{order.item_count}</td>
                        <td className="cell-amount">
                          {order.final_amount
                            ? <><span className="amount-final">₹{order.final_amount}</span><span className="amount-orig">₹{order.total_amount}</span></>
                            : `₹${order.total_amount}`}
                        </td>
                        <td><StatusBadge status={order.status} /></td>
                        <td><StatusBadge status={order.work_status} /></td>
                        <td className="cell-partner">
                          {order.partner_name
                            ? <span title={order.partner_phone}>{order.partner_name}</span>
                            : <span className="unassigned">Unassigned</span>}
                        </td>
                        <td className="cell-actions">
                          <div className="action-group">
                            <button
                              className="action-btn action-assign"
                              title="Assign / Change Partner"
                              onClick={() => setAssignModal({ orderId: order.id, currentPartnerId: order.partner_id })}
                              disabled={order.status === 'cancelled' || order.status === 'completed'}
                            >
                              Assign
                            </button>
                            {order.partner_id && (
                              <button
                                className="action-btn action-remove"
                                title="Remove Partner"
                                onClick={() => setConfirmRemove(order.id)}
                                disabled={order.status === 'cancelled' || order.work_status === 'started' || order.work_status === 'completed'}
                              >
                                Unassign
                              </button>
                            )}
                            <button
                              className="action-btn action-edit"
                              title="Edit Order"
                              onClick={() => setEditModal(order)}
                              disabled={order.status === 'cancelled'}
                            >
                              Edit
                            </button>
                            {order.status !== 'cancelled' && order.status !== 'completed' && (
                              <button
                                className="action-btn action-cancel"
                                title="Cancel Order"
                                onClick={() => setConfirmCancel(order.id)}
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="page-btn"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    ← Prev
                  </button>
                  <span className="page-info">
                    Page {page + 1} of {totalPages} ({total} orders)
                  </span>
                  <button
                    className="page-btn"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      <Footer />

      {/* Modals */}
      {assignModal && (
        <AssignPartnerModal
          orderId={assignModal.orderId}
          currentPartnerId={assignModal.currentPartnerId}
          onClose={() => setAssignModal(null)}
          onAssigned={() => { fetchOrders(); showToast('Partner assigned successfully'); }}
        />
      )}

      {editModal && (
        <EditOrderModal
          order={editModal}
          onClose={() => setEditModal(null)}
          onUpdated={() => { fetchOrders(); showToast('Order updated successfully'); }}
        />
      )}

      {detailDrawer && (
        <OrderDetailDrawer
          orderId={detailDrawer}
          onClose={() => setDetailDrawer(null)}
        />
      )}

      {confirmCancel && (
        <ConfirmDialog
          message={`Are you sure you want to cancel order #${shortId(confirmCancel)}? This cannot be undone.`}
          onConfirm={handleCancelOrder}
          onCancel={() => setConfirmCancel(null)}
          confirming={actionLoading}
        />
      )}

      {confirmRemove && (
        <ConfirmDialog
          message={`Remove the assigned partner from order #${shortId(confirmRemove)}?`}
          onConfirm={handleRemovePartner}
          onCancel={() => setConfirmRemove(null)}
          confirming={actionLoading}
        />
      )}
    </div>
  );
};

export default AdminPage;
